'use client'

import React, { createContext, useContext } from 'react'
import { LazyMotion, domAnimation, MotionConfig } from 'framer-motion'

// ============================================
// ANIMATION PRESETS
// ============================================

export const animationPresets = {
  // Page Transitions
  pageSlideUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
  pageSlideRight: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
  pageFade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2 },
  },

  // Card & Element Animations
  cardHover: {
    scale: 1.02,
    y: -4,
    transition: { type: 'spring', stiffness: 400, damping: 25 },
  },
  cardTap: {
    scale: 0.98,
    transition: { type: 'spring', stiffness: 400, damping: 25 },
  },
  cardEnter: {
    initial: { opacity: 0, y: 20, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },

  // Button Animations
  buttonHover: {
    scale: 1.02,
    transition: { type: 'spring', stiffness: 400, damping: 25 },
  },
  buttonTap: {
    scale: 0.95,
    transition: { type: 'spring', stiffness: 400, damping: 25 },
  },

  // List Stagger
  staggerContainer: {
    animate: {
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1,
      },
    },
  },
  staggerItem: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },

  // Floating Dock
  dockItem: {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    whileHover: { scale: 1.1, y: -2 },
    whileTap: { scale: 0.95 },
    transition: { type: 'spring', stiffness: 400, damping: 25 },
  },

  // Number Counter (for financial data)
  countUp: {
    type: 'spring',
    stiffness: 100,
    damping: 30,
  },

  // Modal & Overlay
  overlay: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2 },
  },
  modal: {
    initial: { opacity: 0, scale: 0.95, y: 20 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.95, y: 20 },
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },

  // Toast Notification
  toast: {
    initial: { opacity: 0, y: 50, scale: 0.9 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 20, scale: 0.9 },
    transition: { type: 'spring', stiffness: 400, damping: 30 },
  },
} as const

// ============================================
// SPRING CONFIGS
// ============================================

export const springConfigs = {
  // Snappy - Quick responses
  snappy: { type: 'spring', stiffness: 400, damping: 25 } as const,

  // Smooth - Gentle movements
  smooth: { type: 'spring', stiffness: 300, damping: 30 } as const,

  // Bouncy - Playful feel
  bouncy: { type: 'spring', stiffness: 500, damping: 20 } as const,

  // Slow - Dramatic effect
  slow: { type: 'spring', stiffness: 100, damping: 20 } as const,
}

// ============================================
// ANIMATION CONTEXT
// ============================================

interface AnimationContextType {
  reducedMotion: boolean
  presets: typeof animationPresets
  springs: typeof springConfigs
}

const AnimationContext = createContext<AnimationContextType>({
  reducedMotion: false,
  presets: animationPresets,
  springs: springConfigs,
})

export const useAnimation = () => useContext(AnimationContext)

// ============================================
// ANIMATION PROVIDER
// ============================================

interface AnimationProviderProps {
  children: React.ReactNode
  reducedMotion?: boolean
}

export function AnimationProvider({ children, reducedMotion = false }: AnimationProviderProps) {
  // Check for user's motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false

  const shouldReduceMotion = reducedMotion || prefersReducedMotion

  return (
    <AnimationContext.Provider
      value={{
        reducedMotion: shouldReduceMotion,
        presets: animationPresets,
        springs: springConfigs,
      }}
    >
      <LazyMotion features={domAnimation}>
        <MotionConfig
          reducedMotion={shouldReduceMotion ? 'always' : 'never'}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
          }}
        >
          {children}
        </MotionConfig>
      </LazyMotion>
    </AnimationContext.Provider>
  )
}

export default AnimationProvider
